import { Calendar, CheckCircle, MapPin, StickyNote, Ticket, User, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';

import { ScanResultState } from './types';
import styles from './PunchupScanResult.module.css';

interface PunchupScanResultProps {
	result: ScanResultState;
	onReset: () => void;
}

const formatDate = (iso: string) =>
	new Date(iso).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
const formatTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

/** Green clone of the dark ScanResultComponent — success / error scan outcome card. Flat solid status
 * headers (no gradients, per DESIGN.md), `--punchup-*` tokens throughout, PunchupButton for the rescan CTA. */
export const PunchupScanResult = ({ result, onReset }: PunchupScanResultProps) => {
	const { t } = useTranslation();

	const rescan = (
		<div className={styles.actions}>
			<PunchupButton
				type="button"
				variant="primary"
				text={t('scanner-rescan', 'Scan another ticket')}
				onClick={onReset}
			/>
		</div>
	);

	if (result.type === 'error') {
		return (
			<div className={styles.card}>
				<div className={`${styles.header} ${styles.headerError}`}>
					<XCircle size={48} aria-hidden />
					<h2 className={styles.title}>{t('scanner-validation-failed', 'Validation failed')}</h2>
				</div>
				<div className={styles.content}>
					{result.errors.map((err, i) => (
						<div className={styles.errorItem} key={i}>
							<p className={styles.errorMessage}>
								{err.error?.Error || t('scanner-unknown-error', 'Unknown error occurred')}
							</p>
							{err.ticket_id && (
								<p className={styles.errorDetails}>
									{t('scanner-ticket-id', 'Ticket ID')}: {err.ticket_id}
								</p>
							)}
						</div>
					))}
				</div>
				{rescan}
			</div>
		);
	}

	const { data } = result;
	const tickets = data.tickets ?? [];
	const event = tickets[0]?.TicketType?.event;

	return (
		<div className={styles.card}>
			<div className={`${styles.header} ${styles.headerSuccess}`}>
				<CheckCircle size={48} aria-hidden />
				<h2 className={styles.title}>{t('scanner-validated', 'Ticket validated!')}</h2>
			</div>

			<div className={styles.content}>
				<div className={styles.userInfo}>
					<User size={20} aria-hidden />
					<h3 className={styles.userName}>{data.user_name || t('scanner-unknown-user', 'Unknown guest')}</h3>
				</div>

				{tickets.map((ticket, i) => (
					<div className={styles.section} key={ticket.ID ?? i}>
						<div className={styles.sectionHeader}>
							<Ticket size={20} aria-hidden />
							<h4 className={styles.sectionTitle}>{ticket.TicketType?.name || t('scanner-ga', 'General Admission')}</h4>
						</div>
						<div className={styles.details}>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>{t('scanner-ticket-id', 'Ticket ID')}</span>
								<span className={styles.detailValue}>{ticket.ID}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>{t('scanner-price', 'Price')}</span>
								<span className={styles.detailValue}>${ticket.TicketType?.initial_price ?? 0}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>{t('scanner-status', 'Scanned status')}</span>
								<span className={styles.detailValue}>
									{ticket.validated ? t('scanner-scanned', 'Scanned') : t('scanner-not-scanned', 'Not scanned')}
								</span>
							</div>
							{ticket.validated && (
								<div className={styles.detailItem}>
									<span className={styles.detailLabel}>{t('scanner-times-scanned', 'Times scanned')}</span>
									<span className={styles.detailValue}>{ticket.validated_times ?? 1}</span>
								</div>
							)}
							{ticket.coupon_code && (
								<div className={styles.detailItem}>
									<span className={styles.detailLabel}>{t('scanner-promo', 'Promotion applied')}</span>
									<span className={`${styles.detailValue} ${styles.detailValueSuccess}`}>
										{ticket.promocode_name || `${t('scanner-code', 'Code')}: ${ticket.coupon_code}`}
									</span>
								</div>
							)}
							{!!ticket.discount_applied && ticket.discount_applied > 0 && (
								<div className={styles.detailItem}>
									<span className={styles.detailLabel}>{t('scanner-discount', 'Discount applied')}</span>
									<span className={`${styles.detailValue} ${styles.detailValueSuccess}`}>
										-${ticket.discount_applied}
									</span>
								</div>
							)}
						</div>
					</div>
				))}

				{data.fan_notes && data.fan_notes.length > 0 && (
					<div className={styles.fanNotes}>
						<div className={styles.fanNotesHeader}>
							<StickyNote size={20} aria-hidden />
							<h4 className={styles.fanNotesTitle}>{t('scanner-fan-notes', 'Fan notes')}</h4>
						</div>
						{data.fan_notes.map((n) => (
							<p className={styles.fanNoteItem} key={n.id}>
								{n.note}
							</p>
						))}
					</div>
				)}

				{event && (
					<div className={styles.section}>
						<div className={styles.sectionHeader}>
							<MapPin size={20} aria-hidden />
							<h4 className={styles.sectionTitle}>{event.name}</h4>
						</div>
						<div className={styles.details}>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>{t('scanner-venue', 'Venue')}</span>
								<span className={styles.detailValue}>{event.venue}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>{t('scanner-address', 'Address')}</span>
								<span className={styles.detailValue}>{event.street_address}</span>
							</div>
							{event.event_days?.[0]?.start_date && (
								<>
									<div className={styles.detailItem}>
										<span className={styles.detailLabel}>{t('scanner-date', 'Date')}</span>
										<span className={styles.detailValue}>
											<Calendar size={16} className={styles.inlineIcon} aria-hidden />
											{formatDate(event.event_days[0].start_date)}
										</span>
									</div>
									<div className={styles.detailItem}>
										<span className={styles.detailLabel}>{t('scanner-time', 'Time')}</span>
										<span className={styles.detailValue}>{formatTime(event.event_days[0].start_date)}</span>
									</div>
								</>
							)}
						</div>
					</div>
				)}
			</div>
			{rescan}
		</div>
	);
};
